/**
 * CSK fork of framework CoralSection using CskCoralLoader.
 */
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import CoralAuthErrorState from "@voxmedia/duet-framework/coral/CoralAuthErrorState";
import CoralLoadingScreen from "@voxmedia/duet-framework/coral/CoralLoadingScreen";
import * as styles from "@voxmedia/duet-framework/coral/CoralSection.css";
import type { CoralContentInit } from "@voxmedia/duet-framework/coral/types";
import { withGracefulErrorBoundary } from "@voxmedia/duet-ui/errors/GracefulErrorBoundary";

import CskCoralLoader, { CoralStreamEmbed } from "./CskCoralLoader";

interface Props {
  contentInit: CoralContentInit;
  onEmbedLoad?: (stream: CoralStreamEmbed) => void;
  containerClassName?: string;
  customScrollerID?: string;
  coralId: string;
  nodeId?: string;
}

const CskCoralSection: FunctionComponent<Props> = (props) => {
  const { coralConfig } = useConfigContext();
  const auth = useAuth();
  const [isCoralLoaded, setIsCoralLoaded] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(true);
  const [authTimedOut, setAuthTimedOut] = useState(false);

  const { onEmbedLoad } = props;
  const streamRef = useRef<CoralStreamEmbed | null>(null);

  const handleEmbedLoad = useCallback(
    (stream: CoralStreamEmbed) => {
      streamRef.current = stream;
      setIsCoralLoaded(true);
      onEmbedLoad?.(stream);
    },
    [onEmbedLoad],
  );

  useEffect(() => {
    if (showLoadingScreen) {
      return;
    }

    const stream = streamRef.current;
    if (!stream || stream.rendered) {
      return;
    }

    stream.render();
  }, [showLoadingScreen]);

  useEffect(() => {
    if (isCoralLoaded && (!auth.userLoading || authTimedOut)) {
      setShowLoadingScreen(false);
    } else {
      setShowLoadingScreen(true);
    }
  }, [auth.userLoading, isCoralLoaded, authTimedOut]);

  useEffect(() => {
    if (!auth.userLoading) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setAuthTimedOut(true);
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, [auth.userLoading]);

  if (!coralConfig) {
    return null;
  }

  return (
    <>
      <CoralAuthErrorState />
      <div
        id={props.coralId}
        className={styles.idHashOffset}
        style={{
          visibility: showLoadingScreen ? "hidden" : "visible",
          opacity: showLoadingScreen ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      <CskCoralLoader
        contentInit={props.contentInit}
        domID={props.coralId}
        onEmbedLoad={handleEmbedLoad}
        containerClassName={props.containerClassName}
        customScrollerID={props.customScrollerID}
        nodeId={props.nodeId}
      />
      {showLoadingScreen && (
        <CoralLoadingScreen
          isAuthenticating={auth.userLoading}
          isCoralLoading={!isCoralLoaded}
        />
      )}
    </>
  );
};

export default withGracefulErrorBoundary(CskCoralSection, {
  context: "CskCoralSectionErrorBoundary",
});
